/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import * as actions from '@src/store/actions';
import Input from '@src/components/common/form/input';
import Button from '@src/components/common/button';
import { EmailInputIcon, PasswordEyeInputIcon, PasswordEyeOpenInputIcon } from '@src/components/common/icon';
import { getThemeSelector } from '@src/store/selectors/theme';
import * as selectors from '@src/store/selectors';
import { Content, StyledForm, InputContent, LoginTypeTabWrapper, LoginTypeTab } from './style';
import Info from './info';

const formSchema = Yup.object().shape({
	email: Yup.string().required('Required'),
	password: Yup.string().required('Required'),
});

const SubmitWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin-top: 32px;
	align-items: center;
`;

const StyleSubmit = styled(Button)`
	width: 50%;
	height: 45px;
	padding: 11px;
`;

const StyleSecondarySubmit = styled.div`
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	width: 50%;
	color: ${props => props.theme.colors.primary};
	&:hover {
		color: ${props => props.theme.colors.primaryDark};
	}
`;

const StyleInput = styled(Input)`
	margin-bottom: ${(props: { isError: string }) => (props.isError ? '7px' : '32px')} !important;
`;

const StylePasswordEyeInputIcon = styled(PasswordEyeInputIcon)`
	&:hover {
		path {
			fill: ${props => props.theme.colors.primaryDark};
		}
	}
`;
const StylePasswordEyeOpenInputIcon = styled(PasswordEyeOpenInputIcon)`
	&:hover {
		path {
			fill: ${props => props.theme.colors.primaryDark};
		}
	}
`;

const hardcodeAccountDomain = [
	'https://ctciuedu-subspace.teaches.cc',
];

const BusinessLogin = (): JSX.Element => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const router = useRouter();
	const { query } = router;
	const domain = useSelector(selectors.getDomain) as string;
	const isHardcodeAccount = hardcodeAccountDomain.indexOf(domain) !== -1;

	const { fromCompanyVat, cb } = query || {};
	const formRef = useRef<FormikProps<any>>(null);
	const errorMessage = useSelector((state: { errorMessage: { groupErrorMessage: string } }) => state.errorMessage.groupErrorMessage);
	const { themes: { default_landing_system } } = useSelector(getThemeSelector) as {
		themes: {
			default_landing_system: string;
		};
	};

	const [isSubmitted, setSubmitted] = useState(false);
	const [passwordType, setPasswordType] = useState('password');

	const handleSubmit = values => {
		let email = values.email.trim();
		if (isHardcodeAccount) {
			email = `ctciuedu+${email}@gmail.com`; // TC-11443
		}
		const password = values.password.trim();
		dispatch(
			actions.emailLoginCall({
				email,
				password,
				...(fromCompanyVat ? { hr_line_uuid: fromCompanyVat } : {}),
				onSuccess: () => {
					if (cb && cb.indexOf('business-login') === -1) {
						router.push(cb as string);
					} else if (default_landing_system === 'hr_system') {
						router.push('/hr');
					} else {
						router.push('/member');
					}
				},
			}),
		);
	};

	const submit = () => {
		if (formRef && formRef.current) {
			setSubmitted(true);
			formRef.current.handleSubmit();
		}
	};

	return (
		<Content>
			<Info />
			<InputContent>
				<Formik
					innerRef={formRef}
					initialValues={{
						email: '',
						password: '',
					}}
					validateOnChange={isSubmitted}
					validateOnBlur={false}
					validationSchema={formSchema}
					onSubmit={values => handleSubmit(values)}
				>
					{({ errors }) => (
						<StyledForm>
							<LoginTypeTabWrapper>
								<LoginTypeTab className="active">
									{t('all:employee_login')}
								</LoginTypeTab>
								<LoginTypeTab onClick={() => window.open('https://teaches.cc/admin/login', '_blank')}>
									{t('all:admin_login')}
								</LoginTypeTab>
							</LoginTypeTabWrapper>
							<StyleInput
								id="email"
								type={isHardcodeAccount ? 'text' : 'email'}
								isError={!!errors.email || errorMessage}
								errorMsg={
									errorMessage ?
										isHardcodeAccount ? t('business:login:hardcode_login_error') : t('business:login:login_error') :
										t('business:required_error_message')
								}
								label={t('business:login:email_label')}
								placeholder={isHardcodeAccount ? t('all:enter_phone_number') : t('business:email_placeholder')}
								icon={<EmailInputIcon />}
							/>
							<StyleInput
								id="password"
								isError={!!errors.password}
								errorMsg={t('business:required_error_message')}
								label={t('business:login:password_label')}
								placeholder={t('business:login:password_placeholder')}
								type={passwordType}
								icon={passwordType === 'password' ?
									<StylePasswordEyeInputIcon /> :
									<StylePasswordEyeOpenInputIcon />}
								iconClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
							/>
							<SubmitWrapper>
								<StyleSecondarySubmit onClick={() => router.push('/business-forget-password')}>
									{t('login:forget')}
								</StyleSecondarySubmit>
								<StyleSubmit handleClick={() => submit()}>
									{t('login:title')}
								</StyleSubmit>
							</SubmitWrapper>
						</StyledForm>
					)}
				</Formik>
			</InputContent>
		</Content>
	);
};

export default BusinessLogin;
